import { useContext, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    AppBar,
    Toolbar,
    Grid,
    IconButton,
    Hidden,
    Box,
    LinearProgress,
    Typography,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    Chip,
    ClickAwayListener
} from '@mui/material'
import { Close } from '@mui/icons-material'
import {
    faBars,
    faCircle,
    faCloudUploadAlt,
    faPauseCircle,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import logo from '../../assets/logo_icv_gris.webp'
import logoNotification from '../../assets/logo_icv_notification_push.webp'
import {
    ConnectionContext,
    useAuth,
    useMachines3DContext,
    useNavigation,
    useReportsContext,
    useExecutionReportContext,
    useSitesContext,
    useUser
} from '../../context'
import useNotification from '../../hooks/useNotification'
import { ROLES_DISPLAY_NAMES } from '../../config'
import { sitesRoutes } from '../../routes'

const obraCache = {}
const obraErrorCache = new Set()
const obraPendingCache = new Set()

const Header = () => {
    const { isOnline } = useContext(ConnectionContext)
    const { handleNavBar } = useNavigation()
    const { userData } = useAuth()
    const { sites, siteSelected } = useSitesContext()
    const { isUniversalInspector, isUniversalChief } = useUser()

    const getUserObrasList = () => {
        if (!userData) return []

        const userRoleName =
			typeof userData.role === 'object' && userData.role !== null
			    ? userData.role.dbName
			    : userData.role

        const isNormalOperator =
			userRoleName === 'inspectionWorker' ||
			userRoleName === 'maintenceOperator' ||
			userRoleName === 'operator' ||
			userRoleName === 'OperatorInspector'

        const isUniversalOrAdmin =
			userRoleName === 'admin' ||
			userRoleName === 'superAdmin' ||
			userRoleName === 'OperatorInspectorUniversal' ||
			userRoleName === 'OperatorMachineryUniversal' ||
			(userData.roles || []).some(
			    (role) =>
			        role === 'admin' ||
					role === 'superAdmin' ||
					role === 'OperatorInspectorUniversal' ||
					role === 'OperatorMachineryUniversal'
			)

        if (isUniversalOrAdmin) {
            const isUniversal = isUniversalInspector || isUniversalChief
            if (isUniversal && siteSelected && siteSelected._id !== 'universal') {
                return [siteSelected.descripcion || 'Todas las obras']
            }
            if (!isUniversal) {
                return ['Todas las obras']
            }
        }

        if (userData.obras && userData.obras.length > 0) {
            const names = userData.obras
                .map((obraId) => {
                    const idToFind =
						typeof obraId === 'object' && obraId !== null
						    ? obraId._id || obraId.idObra || obraId.idobra
						    : obraId

                    const siteFound =
						sites &&
						sites.find(
						    (s) =>
						        s._id === idToFind ||
								s.idObra === idToFind ||
								s.idobra === idToFind
						)

                    if (siteFound) return siteFound.descripcion
                    if (typeof obraId === 'object' && obraId !== null) {
                        if (obraId.descripcion) return obraId.descripcion
                        if (obraId.idObra) return obraId.idObra
                        if (obraId.idobra) return obraId.idobra
                    }
                    const isObjectId =
						typeof idToFind === 'string' && /^[0-9a-fA-F]{24}$/.test(idToFind)
                    if (isObjectId) {
                        if (obraErrorCache.has(idToFind)) return idToFind
                        if (obraCache[idToFind]) return obraCache[idToFind]

                        if (isNormalOperator) {
                            return 'Obra asignada'
                        }

                        if (obraPendingCache.has(idToFind)) return 'Cargando...'
                        obraPendingCache.add(idToFind)

                        sitesRoutes
                            .getSiteById(idToFind)
                            .then((res) => {
                                obraPendingCache.delete(idToFind)
                                if (res?.data?.descripcion) {
                                    obraCache[idToFind] = res.data.descripcion
                                }
                            })
                            .catch(() => {
                                obraPendingCache.delete(idToFind)
                                obraErrorCache.add(idToFind)
                            })
                        return 'Cargando...'
                    }
                    return idToFind
                })
                .filter((name) => name !== null)

            return names.length > 0 ? names : ['Sin obra asignada']
        }

        return ['Sin obra asignada']
    }
    const { message } = useReportsContext()
    const {
        itemsPendingSync,
        uploadProgress,
        isSyncing,
        globalSyncStatus,
        isDownloadingPautas,
        downloadPautasProgress,
        downloadPautasStatusText,
        cancelDownloadPautas
    } = useExecutionReportContext() || {
        itemsPendingSync: 0,
        uploadProgress: 100,
        isSyncing: false,
        globalSyncStatus: [],
        isDownloadingPautas: false,
        downloadPautasProgress: 0,
        downloadPautasStatusText: '',
        cancelDownloadPautas: () => {}
    }

    const [showDetails, setShowDetails] = useState(false)
    const { downloadingData3D, progressDownload3D } = useMachines3DContext()
    const addNotification = useNotification()

    const isSyncingHeader = itemsPendingSync > 0

    const getSyncStatus = () => {
        if (isSyncingHeader) {
            return isOnline
                ? { text: 'Sincronizando...', color: '#1976d2', icon: faCloudUploadAlt }
                : {
                    text: 'Conexión pendiente...',
                    color: '#ed6c02',
                    icon: faPauseCircle
                }
        }

        if (
            message === 'Sincronizado' ||
			message === 'Sincronizado (Modo Offline)'
        ) {
            return {
                text: isOnline ? 'Sincronizado' : 'Sincronizado (Local)',
                color: '#2FB83F',
                icon: faCheckCircle
            }
        }

        return { text: message, color: 'inherit', icon: null }
    }

    const syncStatus = getSyncStatus()

    const messageDownloading =
		downloadingData3D === 'Recursos descargados' ||
		downloadingData3D === 'Modelos 3D descargados.'
		    ? ''
		    : downloadingData3D
		        ? `${downloadingData3D} - ${Math.trunc(progressDownload3D)}%. No desconecte internet`
		        : ''

    const navigate = useNavigate()
    useEffect(() => {
        if (!isOnline) {
            if (addNotification) {
                addNotification({
                    icon: logoNotification,
                    title: 'Alerta',
                    subtitle: 'Pérdida de conexión',
                    message: 'El dispositivo no cuenta con conexión a internet.',
                    theme: 'red',
                    native: true
                })
            }
        }
    }, [isOnline, addNotification])

    return (
        <AppBar
            sx={{
                height: { xs: 60, md: 80 },
                width: '100%',
                backgroundColor: '#fff',
                borderBottomRightRadius: { xs: 15, md: 30 },
                color: '#000'
            }}
        >
            <div className="header-bar">
                <Toolbar style={{ minHeight: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={handleNavBar}>
                        <FontAwesomeIcon icon={faBars} />
                    </IconButton>
                    <img src={logo} className="header-logo" style={{ marginLeft: 10 }} alt="logo" />

                    <Hidden mdDown>
                        {userData && (
                            <div
                                className="user-name"
                                onClick={() => navigate('/user-profile')}
                                style={{ display: 'flex', alignItems: 'center' }}
                            >
                                <div>
                                    <div style={{ margin: 0 }}>
                                        <p className="nombre">
                                            {' '}
                                            {userData.name} {userData.lastName}{' '}
                                        </p>
                                    </div>
                                    <Grid container>
                                        {userData.role && (
                                            <Grid item style={{ marginRight: 10 }}>
                                                <p className="rol">
                                                    {userData.role.name ||
														ROLES_DISPLAY_NAMES[userData.role.dbName] ||
														ROLES_DISPLAY_NAMES[userData.role] ||
														userData.role}
                                                </p>
                                            </Grid>
                                        )}
                                        {userData.roles &&
											userData.roles.map((role, i) => {
											    return (
											        <Grid item key={i} style={{ marginRight: 10 }}>
											            <p className="rol">
											                {ROLES_DISPLAY_NAMES[role] || role}
											            </p>
											        </Grid>
											    )
											})}
                                        <Grid item>
                                            <strong>{messageDownloading}</strong>
                                        </Grid>
                                    </Grid>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        marginLeft: 15
                                    }}
                                >
                                    <p
                                        className="rol"
                                        style={{ fontSize: '0.85em', margin: 0, marginRight: 6 }}
                                    >
										|
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        {getUserObrasList().map((obra, index) => (
                                            <p
                                                key={index}
                                                className="rol"
                                                style={{
                                                    fontSize: '0.75em',
                                                    margin: 0,
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {obra}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Hidden>
                    <div
                        className="header-status"
                        style={{
                            position: 'absolute',
                            right: 10,
                            textAlign: 'right'
                        }}
                    >
                        <ClickAwayListener onClickAway={() => setShowDetails(false)}>
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        justifyContent: 'flex-end',
                                        cursor: globalSyncStatus.length > 0 ? 'pointer' : 'default'
                                    }}
                                    onClick={() =>
                                        globalSyncStatus.length > 0 && setShowDetails(!showDetails)
                                    }
                                >
                                    {syncStatus.icon && (
                                        <FontAwesomeIcon
                                            icon={syncStatus.icon}
                                            size="xs"
                                            color={syncStatus.color}
                                        />
                                    )}
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 'bold', color: syncStatus.color }}
                                    >
                                        {syncStatus.text}
                                    </Typography>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{ mx: 0.5, height: 12, alignSelf: 'center' }}
                                    />
                                    <FontAwesomeIcon
                                        icon={faCircle}
                                        color={isOnline ? '#2FB83F' : '#B62800'}
                                    />
                                    <Typography variant="caption">
                                        {isOnline ? 'Online' : 'Offline'}
                                    </Typography>
                                </Box>

                                {isSyncingHeader && (
                                    <Box sx={{ mt: 0.5, minWidth: 200, position: 'relative' }}>
                                        {/* Master Progress Bar */}
                                        <LinearProgress
                                            variant="determinate"
                                            value={uploadProgress}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                bgcolor: '#e0e0e0',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 2,
                                                    bgcolor: syncStatus.color
                                                }
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mt: 0.5
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{ fontSize: '0.6rem', color: 'text.secondary' }}
                                            >
                                                {itemsPendingSync > 0
                                                    ? `${itemsPendingSync} items pendientes`
                                                    : 'Finalizando sincronización...'}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '0.65rem',
                                                    color: '#1976d2',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    textDecoration: 'underline',
                                                    ml: 1,
                                                    '&:hover': { color: '#115293' }
                                                }}
                                                onClick={() => setShowDetails(!showDetails)}
                                            >
                                                {showDetails ? 'ocultar detalles' : 'ver detalles'}
                                            </Typography>
                                        </Box>

                                        {showDetails && globalSyncStatus.length > 0 && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    right: 0,
                                                    mt: 1.5,
                                                    width: 220,
                                                    maxHeight: 180,
                                                    overflowY: 'auto',
                                                    bgcolor: '#fff',
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    border: '1px solid #e0e0e0',
                                                    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                                                    zIndex: 3000,
                                                    '&::-webkit-scrollbar': { width: '4px' },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        bgcolor: '#ccc',
                                                        borderRadius: '4px'
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {globalSyncStatus.map((item, index) => {
                                                    const itemProgress =
														item.total === 0
														    ? 100
														    : Math.round((item.processed / item.total) * 100)
                                                    return (
                                                        <Box
                                                            key={item.id}
                                                            sx={{ mb: 1.5, textAlign: 'left' }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    mb: 0.5
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '0.7rem',
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                >
																	OT {item.otId}
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '0.65rem',
                                                                        color: 'text.secondary'
                                                                    }}
                                                                >
                                                                    {item.progress} ({itemProgress}%)
                                                                </Typography>
                                                            </Box>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={itemProgress}
                                                                sx={{
                                                                    height: 4,
                                                                    borderRadius: 2,
                                                                    bgcolor: '#f0f0f0',
                                                                    '& .MuiLinearProgress-bar': {
                                                                        bgcolor:
																			item.status === 'sincronizado'
																			    ? '#2FB83F'
																			    : syncStatus.color
                                                                    }
                                                                }}
                                                            />
                                                            {index < globalSyncStatus.length - 1 && (
                                                                <Divider sx={{ mt: 1, opacity: 0.4 }} />
                                                            )}
                                                        </Box>
                                                    )
                                                })}
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {isDownloadingPautas && (
                                    <Box sx={{ mt: 0.5, minWidth: 240, position: 'relative' }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={downloadPautasProgress}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                bgcolor: '#e0e0e0',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 2,
                                                    bgcolor: '#1976d2'
                                                }
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mt: 0.5,
                                                gap: '4px'
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '0.65rem',
                                                    color: 'text.secondary',
                                                    fontWeight: 'bold',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '180px'
                                                }}
                                                title={downloadPautasStatusText}
                                            >
                                                {downloadPautasStatusText || 'Descargando pautas...'}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: '0.65rem',
                                                        color: '#1976d2',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {downloadPautasProgress}%
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={cancelDownloadPautas}
                                                    sx={{
                                                        padding: 0,
                                                        color: '#d32f2f',
                                                        '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                                                    }}
                                                    title="Parar descarga"
                                                >
                                                    <Close sx={{ fontSize: '12px' }} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </ClickAwayListener>
                        {!isOnline && !isSyncingHeader && (
                            <Typography
                                variant="caption"
                                display="block"
                                color="error"
                                sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                            >
								Sin red - Modo local
                            </Typography>
                        )}
                    </div>
                </Toolbar>
            </div>
        </AppBar>
    )
}

export default Header
